// FAQPage.js — FAQ with a search box, category filters, and accordion
// answers. The FAQPage JSON-LD is generated from the same FAQS array.

import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Container,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import SEO from "./components/SEO";
import { InternalLink, PageHeader } from "./components/Prose";
import { BRAND } from "./theme";

// Flatten any React node (string, number, element, array, fragment) into
// a plain string for JSON-LD. Drops attribute values; keeps text content
// so the FAQPage schema stays readable.
function reactNodeToText(node) {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(reactNodeToText).join("");
  if (React.isValidElement(node)) {
    return reactNodeToText(node.props?.children);
  }
  return "";
}

const CATEGORIES = ["All", "Basics", "Data", "Stats", "Project"];

const FAQS = [
  {
    category: "Basics",
    q: "What is a scorigami?",
    a: (
      <>
        A scorigami is a final score that has never happened before. The term
        was coined by the writer Jon Bois for American football and has since
        been applied across many sports. On this site, the empty squares are
        results the Premier League has never produced, and a result that has
        happened exactly once is marked as a scorigami: the day it happened, it
        was brand new.
      </>
    ),
  },
  {
    category: "Basics",
    q: "What is the difference between the Home / Away and Winner / Loser views?",
    a: (
      <>
        Home / Away, the default, puts the home team's goals along the bottom
        and the away team's goals up the side, so a 3–1 home win and a 1–3
        away win are separate squares. Winner / Loser ignores venue. Read it
        from either team's point of view: goals scored along the bottom, goals
        conceded up the side. Each result therefore appears in two mirrored
        squares, one for each team, and both show the same total.
      </>
    ),
  },
  {
    category: "Basics",
    q: "Why track home and away results separately?",
    a: (
      <>
        Because it shows that new results are still being made. Ignoring
        venue, the Premier League has not produced a new scoreline since West
        Brom 5–5 Manchester United in May 2013. Split by home and away, there
        have been three since 2019: Southampton 0–9 Leicester City, Crystal
        Palace 0–7 Liverpool and Sheffield United 0–8 Newcastle United. Many
        open squares remain within reach. No team has ever won 7–2 away from
        home, for example, although it has happened five times at home.
      </>
    ),
  },
  {
    category: "Basics",
    q: "Are draws included?",
    a: (
      <>
        Yes. Draws sit on the diagonal, where both scores are equal. 1–1 and
        0–0 are among the most common results in the competition, 4–4 has
        happened 18 times, and 5–5 only once: West Brom against Manchester
        United in May 2013.
      </>
    ),
  },
  {
    category: "Stats",
    q: "Why are low-scoring results so much more common?",
    a: (
      <>
        Goals are scarce in football. The four most common Premier League
        results are 1–0, 2–1, 2–0 and 1–1, and only around one match in forty
        produces seven or more goals. Ten or more is rarer still: it has
        happened in six matches, and each of those scorelines has happened
        only once.
      </>
    ),
  },
  {
    category: "Stats",
    q: "What is the rarest Premier League scoreline?",
    a: (
      <>
        Six scorelines have happened exactly once: 7–4, 6–4, 9–1, 8–2, 7–3 and
        5–5, all between 2007 and 2013. Counting home and away separately,
        eleven results are one-offs. The{" "}
        <InternalLink to="/records">Records page</InternalLink> lists each one
        with the match it came from.
      </>
    ),
  },
  {
    category: "Stats",
    q: "What is the highest-scoring Premier League match?",
    a: (
      <>
        Portsmouth 7–4 Reading on September 29, 2007. Its eleven goals are a
        Premier League record, and no other match has produced more than ten.
        The record winning margin is nine goals, reached four times:
        Manchester United 9–0 Ipswich Town (1995), Southampton 0–9 Leicester
        City (2019), Manchester United 9–0 Southampton (2021) and Liverpool 9–0
        Bournemouth (2022).
      </>
    ),
  },
  {
    category: "Stats",
    q: "Why do the colors change in the single-season view?",
    a: (
      <>
        Colors are grouped into bands, and the bands fit the view. All-time
        counts run into the thousands (Once, 2–9, 10–49, up to 1,000+), while a
        single season tops out in the tens (Once, 2, 3–4, up to 20+). Every
        square also shows its exact count, and the legend always lists the
        bands in use.
      </>
    ),
  },
  {
    category: "Data",
    q: "What period does the data cover?",
    a: (
      <>
        Every Premier League match from the first season, 1992/93, to the most
        recent completed match: more than 13,000 in total. Matches from the
        old First Division, before the Premier League began, are not included.
      </>
    ),
  },
  {
    category: "Data",
    q: "How quickly are new results added?",
    a: (
      <>
        During matchdays the site checks for finished matches every ten
        minutes, so new results usually appear within about 15 minutes of the
        final whistle. If a score is corrected after full time, for example
        when a late goal is confirmed, the stored result is updated
        automatically.
      </>
    ),
  },
  {
    category: "Data",
    q: "Where does the data come from?",
    a: (
      <>
        Results come from football-data.org. The complete history has been
        checked, match by match, against two independent archives:
        football-data.co.uk and the openfootball project. Club crests are also
        supplied by football-data.org.
      </>
    ),
  },
  {
    category: "Project",
    q: "Can I share a scoreline?",
    a: (
      <>
        Yes. Select any square to open its details, then choose Share on X for
        a ready-written post, or the download button for a square image of the
        result, sized for social media.
      </>
    ),
  },
  {
    category: "Project",
    q: "Is there an X account?",
    a: (
      <>
        Yes.{" "}
        <a
          href="https://x.com/pl_gami"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: BRAND.cyan, fontWeight: 600 }}
        >
          @pl_gami
        </a>{" "}
        posts every Premier League result automatically as matches finish,
        with extra detail when a result is rare or has never happened before,
        and a round-up of each day's results.
      </>
    ),
  },
  {
    category: "Project",
    q: "Who runs this site?",
    a: (
      <>
        Premier League Scorigami is an independent project, built and
        maintained by Mason Maier. It is not affiliated with the Premier
        League or any club. Corrections and suggestions are welcome through
        the <InternalLink to="/contact">Contact page</InternalLink>.
      </>
    ),
  },
];

export default function FAQPage() {
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FAQS.filter((f) => {
      if (category !== "All" && f.category !== category) return false;
      if (!q) return true;
      const hay = `${f.q} ${reactNodeToText(f.a)}`.toLowerCase();
      return hay.includes(q);
    });
  }, [category, query]);

  return (
    <>
      <SEO
        title="What is scorigami? Premier League Scorigami FAQ"
        description="What scorigami means in football, how the Premier League scoreline grid works, where the data comes from, how often it updates and what @pl_gami posts."
        path="/faq"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: {
              "@type": "Answer",
              text: reactNodeToText(f.a).replace(/\s+/g, " ").trim(),
            },
          })),
        }}
      />
      <Container maxWidth="md" sx={{ py: { xs: 3, sm: 5 } }}>
      <PageHeader
        eyebrow="FAQ"
        title="Frequently asked questions"
        lede="How the grid works, where the data comes from, and how to follow new results as they happen."
      />

      {/* Search */}
      <TextField
        fullWidth
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search the FAQ…"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: BRAND.cyan }} />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 2.5,
          "& .MuiOutlinedInput-root": {
            borderRadius: 999,
            background: "rgba(20,20,26,0.7)",
            backdropFilter: "blur(10px)",
            "& fieldset": { borderColor: BRAND.divider },
            "&:hover fieldset": { borderColor: BRAND.cyan },
            "&.Mui-focused fieldset": { borderColor: BRAND.cyan },
          },
          "& input": { color: "#fff" },
        }}
      />

      {/* Category chips */}
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        sx={{ mb: 3 }}
      >
        {CATEGORIES.map((c) => {
          const active = category === c;
          return (
            <Chip
              key={c}
              label={c}
              clickable
              onClick={() => setCategory(c)}
              sx={{
                fontWeight: 600,
                letterSpacing: "0.04em",
                color: active ? "#fff" : BRAND.textSecondary,
                background: active
                  ? BRAND.gradient
                  : "rgba(20,20,26,0.7)",
                border: `1px solid ${active ? "transparent" : BRAND.divider}`,
                "&:hover": {
                  background: active
                    ? BRAND.gradient
                    : "rgba(57,208,255,0.08)",
                  color: "#fff",
                },
              }}
            />
          );
        })}
      </Stack>

      {/* Accordion list */}
      {filtered.length === 0 ? (
        <Box
          sx={{
            py: 6,
            textAlign: "center",
            color: BRAND.textSecondary,
            border: `1px dashed ${BRAND.divider}`,
            borderRadius: 3,
          }}
        >
          <Typography>No questions match that search.</Typography>
        </Box>
      ) : (
        filtered.map((f, i) => (
          <Accordion
            key={f.q}
            slots={{ heading: "h2" }}
            disableGutters
            sx={{
              mb: 1.25,
              background:
                "linear-gradient(180deg, rgba(25,25,32,0.8) 0%, rgba(18,18,24,0.8) 100%)",
              border: `1px solid ${BRAND.divider}`,
              borderRadius: "14px !important",
              overflow: "hidden",
              boxShadow: "none",
              "&:before": { display: "none" },
              "&.Mui-expanded": {
                borderColor: BRAND.cyan,
                boxShadow: "0 6px 26px rgba(57,208,255,0.15)",
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: BRAND.cyan }} />}
              sx={{
                px: { xs: 2, sm: 3 },
                py: 0.5,
                "& .MuiAccordionSummary-content": { my: 1.25 },
              }}
            >
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: { xs: 14.5, sm: 16 },
                  letterSpacing: "-0.005em",
                }}
              >
                {f.q}
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                px: { xs: 2, sm: 3 },
                pt: 0,
                pb: 2.5,
                color: BRAND.textSecondary,
                fontSize: 15,
                lineHeight: 1.75,
                borderTop: `1px solid ${BRAND.divider}`,
              }}
            >
              <Box sx={{ pt: 1.5 }}>{f.a}</Box>
            </AccordionDetails>
          </Accordion>
        ))
      )}
      </Container>
    </>
  );
}
